import { ExtendedSelection, Layout, Text } from '@oneaudi/unified-web-alpha-components';
import { htmlToText } from 'html-to-text';
import { ReactNode } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { VATConfig } from '~/app/market-config';
import { useDebugMode } from '~/app/utils/debug-mode';
import { Prices } from '~/components/prices';
import { CardContent } from '~/components/selection-card-new/card-content';
import { PriceOrError } from '~/graphql/mapper';
import { CurrencyFormatterDisplayContext } from '~/hooks/use-currency-formatter';
import { DATA_TEST_ID } from '~/test/data-test-id';

type CardItemInfo = {
	name: string;
	selected: boolean;
	loading: boolean;
	readonly: boolean;
	standard: boolean;
	trimOrDrivetrain: 'trimline' | 'drivetrain';
	onClick: () => void;
	price?: {
		totalPrice?: PriceOrError;
		monthlyRate?: number | undefined;
		trimlineAndEngine: VATConfig;
		displayContext?: CurrencyFormatterDisplayContext | undefined;
	};
	incentives?: ReactNode;
	prCode?: string;
	year?: string;
	debugCode?: string;
	onDescriptionToggle: (value: boolean) => void;
};

export const TrimlineSelectionCard = ({
	item: {
		name,
		selected,
		readonly,
		debugCode,
		price,
		standard,
		onClick,
		incentives,
		prCode,
		year,
		onDescriptionToggle,
		trimOrDrivetrain,
	},
	textLinks,
	children,
	id,
	isOnlyOption,
}: {
	item: CardItemInfo;
	children?: ReactNode;
	textLinks?: ReactNode;
	id: string;
	isOnlyOption?: boolean;
}) => {
	const { i18n } = useI18n();
	const { isDebug } = useDebugMode();
	const code = `[${debugCode ?? `${prCode}-${year}`}]`;
	const displayName = isDebug ? `${name} ${code}` : name;

	const priceComponent = price ? (
		<div style={{ marginLeft: 0 }}>
			{standard ? (
				<Text id={`${id}-price-information`} tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					{i18n('ngc.common.standard')}
				</Text>
			) : (
				<Prices
					id={`${id}-price-information`}
					totalPrice={price.totalPrice}
					monthlyRateOverride={price.monthlyRate}
					vatConfig={price.trimlineAndEngine}
					displayContext={price.displayContext}
					size="3"
					displayMode="rows"
				/>
			)}
			{incentives}
		</div>
	) : null;

	const content: Array<ReactNode> = [];

	const cardContent = (
		<Layout direction="column" rowGap="16px">
			{priceComponent}
			<CardContent
				key={`${id}-card-content`}
				onDescriptionToggle={onDescriptionToggle}
				alwaysVisible={selected}
				linksSection={textLinks}>
				{children}
			</CardContent>
		</Layout>
	);

	content.push(cardContent);

	return (
		<ExtendedSelection
			type={isOnlyOption ? 'multi-select' : 'single-select'}
			name={trimOrDrivetrain}
			checked={selected}
			disabled={readonly}
			value={code}
			label={htmlToText(displayName)}
			id={id}
			content={content}
			data-testid={DATA_TEST_ID.SELECTION_CARD_CONTAINER}
			aria-describedby={`${id}-price-information`}
			onClick={onClick}
		/>
	);
};
